(function () {
    'use strict';

    var dialog = null;
    var closeTriggers = [];
    var form = null;
    var shopifyForm = null;
    var successState = null;
    var ratingInput = null;
    var submitButton = null;
    var starsWrap = null;
    var starButtons = [];
    var ctaButtons = [];
    var lastOpenTrigger = null;
    var statusMessage = null;
    var isSubmitting = false;
    var shopifyReadinessIssue = '';
    var bridgeVersion = '2026-10-06.8';

    function getShopifyShadowRoots() {
        var roots = [];
        function collect(container) {
            Array.prototype.forEach.call(container.querySelectorAll('*'), function (element) {
                if (element.shadowRoot) {
                    roots.push(element.shadowRoot);
                    collect(element.shadowRoot);
                }
            });
        }
        if (shopifyForm) collect(shopifyForm);
        return roots;
    }

    function debugShopifyForm() {
        var target = getShopifyFields();
        return {
            version: bridgeVersion,
            containerFound: Boolean(shopifyForm),
            containerConnected: Boolean(shopifyForm && shopifyForm.isConnected),
            ready: Boolean(target),
            issue: shopifyReadinessIssue,
            shadowRoots: getShopifyShadowRoots().map(function (root) {
                return {
                    host: root.host.tagName,
                    hostId: root.host.id,
                    fieldIds: Array.prototype.map.call(root.querySelectorAll('input, textarea, select'), function (field) {
                        return field.id;
                    }),
                    fields: Array.prototype.map.call(root.querySelectorAll('input, textarea, select'), function (field) {
                        return { tag: field.tagName, id: field.id, name: field.name, type: field.type };
                    }),
                    buttonCount: root.querySelectorAll('button').length
                };
            })
        };
    }

    // Read-only diagnostics; never include customer field values.
    window.mbReviewFormDebug = debugShopifyForm;

    function setStatus(message) {
        if (!statusMessage) return;
        statusMessage.textContent = message;
        statusMessage.hidden = !message;
    }

    function getShopifyFields() {
        if (!shopifyForm) {
            shopifyReadinessIssue = 'Missing [data-shopify-form] container in the review section.';
            return null;
        }

        var roots = getShopifyShadowRoots();
        shopifyReadinessIssue = 'No open shadow root found inside [data-shopify-form].';

        var fieldIds = { name: 'first_name', email: 'email', rating: 'custom#rating', text: 'custom#message', product: 'custom#product' };
        var fewestMissing = Object.keys(fieldIds).length + 1;
        for (var i = 0; i < roots.length; i++) {
            var root = roots[i];
            var fields = {};
            var missing = [];
            Object.keys(fieldIds).forEach(function (key) {
                fields[key] = key === 'text'
                    ? root.querySelector('textarea[name="custom#message"]')
                    : root.getElementById(fieldIds[key]);
                if (!fields[key]) missing.push(fieldIds[key]);
            });
            if (missing.length) {
                if (missing.length < fewestMissing) {
                    fewestMissing = missing.length;
                    shopifyReadinessIssue = 'Shadow root is missing review fields: ' + missing.join(', ');
                }
                continue;
            }

            // Some app versions render their controls without an HTML <form>.
            var nativeForm = fields.email.closest('form');
            var scope = nativeForm || fields.email.closest('[role="form"]') || root;
            var button = scope.querySelector('button[type="submit"]') || scope.querySelector('button');
            if (!button) {
                shopifyReadinessIssue = 'Review fields were found, but their submit button is missing.';
                continue;
            }
            shopifyReadinessIssue = '';
            return { root: root, fields: fields, form: nativeForm, button: button };
        }
        return null;
    }

    function waitForShopifyFields() {
        var started = Date.now();
        return new Promise(function (resolve, reject) {
            function check() {
                var target = getShopifyFields();
                if (target) return resolve(target);
                if (Date.now() - started >= 5000) {
                    return reject(new Error('The Shopify review form is not ready. Please try again.'));
                }
                window.setTimeout(check, 100);
            }
            check();
        });
    }

    function setShopifyFieldValue(field, value) {
        // Use the native setter so the app's controlled fields receive the change.
        var prototype = Object.getPrototypeOf(field);
        var descriptor = Object.getOwnPropertyDescriptor(prototype, 'value');
        if (descriptor && descriptor.set) descriptor.set.call(field, String(value));
        else field.value = String(value);
        field.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
        field.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
    }

    function prefillShopifyProductWhenReady() {
        var productId = shopifyForm ? shopifyForm.getAttribute('data-product-id') : '';
        if (!productId) return;
        var started = Date.now();

        function check() {
            var roots = getShopifyShadowRoots();
            for (var i = 0; i < roots.length; i++) {
                var productField = roots[i].getElementById('custom#product');
                if (productField) {
                    setShopifyFieldValue(productField, productId);
                    return;
                }
            }
            if (Date.now() - started < 10000) window.setTimeout(check, 100);
            else console.error('[mb-review-form ' + bridgeVersion + '] Product field did not load for prefilling.');
        }

        check();
    }

    function showSubmissionMessage() {
        form.setAttribute('aria-busy', 'false');
        setStatus('');
        form.hidden = true;
        if (successState) {
            successState.hidden = false;
            var closeButton = successState.querySelector('[data-mb-review-dialog-close]');
            if (closeButton) closeButton.focus();
        }
    }

    async function submitToShopify(payload) {
        if (isSubmitting) return;
        isSubmitting = true;
        updateSubmitState();
        setStatus('');

        try {
            var target = await waitForShopifyFields();
            payload.product = shopifyForm.getAttribute('data-product-id');
            if (!payload.product) throw new Error('The review product ID is missing.');
            Object.keys(target.fields).forEach(function (key) {
                // Product is already in the app's state from initialization.
                if (key === 'product') return;
                setShopifyFieldValue(target.fields[key], payload[key]);
            });
            // Let the app process its input events before reading its current submit control.
            await new Promise(function (resolve) { window.setTimeout(resolve, 0); });
            target = getShopifyFields();
            if (!target) {
                throw new Error('The Shopify review form is not ready. Please try again.');
            }
            var invalid = target.form ? target.form.querySelector(':invalid') : null;
            Object.keys(target.fields).forEach(function (key) {
                var field = target.fields[key];
                if (!invalid && typeof field.checkValidity === 'function' && !field.checkValidity()) invalid = field;
            });
            if (invalid || (target.form && !target.form.checkValidity())) {
                throw new Error(invalid ? invalid.validationMessage : 'Please check your review details.');
            }
            if (target.button.disabled) throw new Error('Shopify cannot accept the review yet. Please try again.');
            // Preserve Shopify's click/submit handlers, validation and request processing.
            target.button.click();
            document.dispatchEvent(new CustomEvent('mb:review-form-submit', { detail: payload }));
            showSubmissionMessage();
        } catch (error) {
            console.error('[mb-review-form ' + bridgeVersion + ']', error, debugShopifyForm());
            isSubmitting = false;
            setStatus('The review could not be submitted. Please try again.');
            updateSubmitState();
        }
    }

    function openDialog(trigger) {
        if (!dialog) return;
        lastOpenTrigger = trigger || null;
        if (form) {
            form.hidden = false;
        }
        if (successState) {
            successState.hidden = true;
        }
        dialog.hidden = false;
        document.body.style.overflow = 'hidden';
        var firstInput = form ? form.querySelector('input[name="name"]') : null;
        if (firstInput) firstInput.focus();
    }

    function closeDialog() {
        if (!dialog) return;
        dialog.hidden = true;
        document.body.style.overflow = '';
        if (lastOpenTrigger) lastOpenTrigger.focus();
    }

    function renderStars(value) {
        var numericValue = Number(value) || 0;

        starButtons.forEach(function (button) {
            var starValue = Number(button.getAttribute('data-rating-value')) || 0;
            var isActive = starValue <= numericValue;
            button.classList.toggle('is-active', isActive);
            button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
    }

    function setRating(value) {
        var numericValue = Number(value) || 0;
        if (ratingInput) {
            ratingInput.value = String(numericValue);
            ratingInput.setCustomValidity('');
        }

        renderStars(numericValue);
        updateSubmitState();
    }

    function hasRequiredFieldsFilled() {
        if (!form) return false;

        var nameValue = form.elements.namedItem('name').value.trim();
        var ratingValue = Number(ratingInput ? ratingInput.value : 0);

        return nameValue.length > 0 && ratingValue >= 1 && ratingValue <= 5 && form.checkValidity();
    }

    function updateSubmitState() {
        if (!submitButton) return;

        var canSubmit = !isSubmitting && hasRequiredFieldsFilled();
        submitButton.disabled = !canSubmit;
        submitButton.setAttribute('aria-disabled', canSubmit ? 'false' : 'true');
        form.setAttribute('aria-busy', isSubmitting ? 'true' : 'false');
    }

    function gatherFormData() {
        if (!form) return null;

        return {
            name: form.elements.namedItem('name').value.trim(),
            email: form.elements.namedItem('email').value.trim(),
            text: form.elements.namedItem('text').value.trim(),
            rating: Number(ratingInput ? ratingInput.value : 0)
        };
    }

    function validateRequiredFields() {
        if (!form) return false;

        var ratingValue = Number(ratingInput ? ratingInput.value : 0);

        if (!form.reportValidity()) {
            return false;
        }

        if (ratingInput) {
            ratingInput.setCustomValidity('');
            if (ratingValue < 1 || ratingValue > 5) {
                ratingInput.setCustomValidity('Please select a rating.');
                if (typeof ratingInput.reportValidity === 'function') {
                    ratingInput.reportValidity();
                }
                return false;
            }
        }

        return true;
    }

    function bindEvents() {
        ctaButtons.forEach(function (button) {
            button.addEventListener('click', function () {
                openDialog(button);
            });
        });

        closeTriggers.forEach(function (button) {
            button.addEventListener('click', closeDialog);
        });

        starButtons.forEach(function (button) {
            button.addEventListener('mouseenter', function () {
                var value = button.getAttribute('data-rating-value');
                renderStars(value);
            });

            button.addEventListener('focus', function () {
                var value = button.getAttribute('data-rating-value');
                renderStars(value);
            });

            button.addEventListener('click', function () {
                var value = button.getAttribute('data-rating-value');
                setRating(value);
            });
        });

        if (starsWrap) {
            starsWrap.addEventListener('mouseleave', function () {
                var selectedRating = ratingInput ? ratingInput.value : 0;
                renderStars(selectedRating);
            });
        }

        if (form) {
            form.addEventListener('input', updateSubmitState);

            form.addEventListener('submit', function (event) {
                event.preventDefault();
                if (!validateRequiredFields()) return;
                submitToShopify(gatherFormData());
            });
        }

        document.addEventListener('keydown', function (event) {
            if (!dialog || dialog.hidden) return;
            if (event.key === 'Escape') {
                closeDialog();
            }
        });
    }

    function init() {
        dialog = document.getElementById('mb-review-form-dialog');
        if (!dialog) return;

        closeTriggers = Array.prototype.slice.call(dialog.querySelectorAll('[data-mb-review-dialog-close]'));
        form = dialog.querySelector('[data-mb-review-form]');
        var section = dialog.closest('.shopify-section');
        shopifyForm = section ? section.querySelector('[data-shopify-form]') : null;
        successState = dialog.querySelector('[data-mb-review-success]');
        statusMessage = dialog.querySelector('[data-mb-review-status]');
        ratingInput = dialog.querySelector('[data-mb-review-rating-input]');
        submitButton = form ? form.querySelector('.mb-review-form__submit') : null;
        starsWrap = dialog.querySelector('[data-mb-review-stars]');
        starButtons = Array.prototype.slice.call(dialog.querySelectorAll('[data-rating-value]'));
        ctaButtons = Array.prototype.slice.call(document.querySelectorAll('.mb-product-reviews__cta'));

        setRating(0);
        updateSubmitState();
        bindEvents();
        prefillShopifyProductWhenReady();
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
